import { type NormalizedCacheObject } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { useEffect } from 'react'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { LicenseInfo } from '@mui/x-license'
import { Snackbars } from 'settle-ui/components/Snackbars/Snackbars'
import { AppProgress } from 'routes/components/AppProgress/AppProgress'
import { isTrackingDisabled } from 'lib/tracking/segment'
import { qsSchema } from 'lib/zod'
import { initDatadog } from 'lib/tracking/datadog'
import { initBusinessStorage } from 'lib/helpers/createPersistedBusinessStore/createPersistedBusinessStore'
import { isImpersonationSession } from 'lib/helpers/isImpersonationSession/isImpersonationSession'
import { hasSecretPath } from 'lib/helpers/redactUrlSecrets/redactUrlSecrets'
import { setTimeZonePreferenceSession } from 'lib/helpers/timeZonePreferenceSession/timeZonePreferenceSession'
import { setActiveTimeZone, initActiveTimeZone } from 'lib/hooks/useActiveTimeZone/useActiveTimeZone'
import { setScrollbarStrategy } from './helpers/setScrollbarStrategy/setScrollbarStrategy'
import { useSetProfileIdCookie } from './hooks/useSetProfileIdCookie/useSetProfileIdCookie'
import { useLogSnackbarEvent } from './hooks/useLogSnackbarEvent/useLogSnackbarEvent'
import { AppStylesProvider, type AppStylesProviderProps } from './components/AppStylesProvider/AppStylesProvider'
import { BusinessIdProvider } from './components/BusinessIdProvider/BusinessIdProvider'
import { SegmentIdentityTracking } from './components/SegmentIdentityTracking/SegmentIdentityTracking'
import { DatadogIdentityTracking } from './components/DatadogIdentityTracking/DatadogIdentityTracking'
import { SocureDeviceInitializer } from './components/SocureDeviceInitializer/SocureDeviceInitializer'
import { TimeZonePreferenceInitializer } from './components/TimeZonePreferenceInitializer/TimeZonePreferenceInitializer'
import { GlobalStyles } from './components/GlobalStyles/GlobalStyles'
import * as events from './AppPage.events'
import { useInitializedApolloClient } from './hooks/useInitializedApolloClient/useInitializedApolloClient'
import { useTrackRouterTimings } from './hooks/useTrackRouterTimings/useTrackRouterTimings'
import { useSessionReplayGuard } from './hooks/useSessionReplayGuard/useSessionReplayGuard'
import { useRecoverFromStaleDeployErrors } from './hooks/useRecoverFromStaleDeployErrors/useRecoverFromStaleDeployErrors'
import { useRedirectOnTabUserSwitch } from './hooks/useRedirectOnTabUserSwitch/useRedirectOnTabUserSwitch'
import { ImpersonationIndicatorDynamic } from './components/ImpersonationIndicator/ImpersonationIndicatorDynamic'
import { PageErrorBoundary } from './components/PageErrorBoundary/PageErrorBoundary'
import { VercelAnalytics } from './components/VercelAnalytics/VercelAnalytics'
import {
  type FeatureFlagsProviderProps,
  FeatureFlagsProvider,
} from './components/FeatureFlagsProvider/FeatureFlagsProvider'

LicenseInfo.setLicenseKey(process.env.MUI_LICENSE_KEY ?? '')

const SOCURE_SDK_KEY = process.env.NEXT_PUBLIC_SOCURE_SDK_KEY

export type AppPageProps = AppStylesProviderProps &
  AppProps<{
    businessId: Maybe<string>
    profileId: Maybe<string>
    initialCache?: NormalizedCacheObject
    initialFlags?: FeatureFlagsProviderProps
  }>

export const AppPage = ({ Component, pageProps, router, emotionCache }: AppPageProps) => {
  const { profileId, initialCache, businessId: ssrBusinessId = null, initialFlags } = pageProps
  const businessId = qsSchema.parse(router.query.businessId) || ssrBusinessId
  const identifyUserInSegment = !isTrackingDisabled() && profileId
  const apolloClient = useInitializedApolloClient({ businessId, initialCache, pathName: router.pathname })

  useSetProfileIdCookie(profileId)
  useTrackRouterTimings()
  useSessionReplayGuard()
  useRecoverFromStaleDeployErrors()
  useRedirectOnTabUserSwitch()
  useEffect(initDatadog, [])
  useEffect(initActiveTimeZone, [])
  useEffect(() => {
    const sessionChanged = setTimeZonePreferenceSession(profileId ?? null)

    if (sessionChanged || !profileId) {
      setActiveTimeZone(null)
    }
  }, [profileId])
  useEffect(() => initBusinessStorage(businessId), [businessId])
  useEffect(setScrollbarStrategy, [])

  const socureCollectionAllowed = !hasSecretPath({ url: router.asPath })

  const logSnackbarDisplayed = useLogSnackbarEvent(router, events.SNACKBAR_DISPLAYED)
  const logSnackbarClicked = useLogSnackbarEvent(router, events.SNACKBAR_ACTION_BTN_CLICK)

  return (
    <FeatureFlagsProvider {...initialFlags}>
      <Head>
        <title>Settle</title>
      </Head>
      <VercelAnalytics />
      <ApolloProvider client={apolloClient}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AppStylesProvider emotionCache={emotionCache}>
            <BusinessIdProvider businessId={businessId}>
              {identifyUserInSegment && <SegmentIdentityTracking profileId={profileId} />}
              {profileId && <DatadogIdentityTracking profileId={profileId} />}
              {profileId && <TimeZonePreferenceInitializer profileId={profileId} />}
              {SOCURE_SDK_KEY && (
                <SocureDeviceInitializer sdkKey={SOCURE_SDK_KEY} collectionAllowed={socureCollectionAllowed} />
              )}
              <GlobalStyles />
              <Snackbars onMount={logSnackbarDisplayed} onActionClick={logSnackbarClicked} />
              <AppProgress />
              {isImpersonationSession() && <ImpersonationIndicatorDynamic />}
              <PageErrorBoundary>
                <Component {...pageProps} />
              </PageErrorBoundary>
            </BusinessIdProvider>
          </AppStylesProvider>
        </LocalizationProvider>
      </ApolloProvider>
    </FeatureFlagsProvider>
  )
}
